/**
 * Comptage du tiroir COUPURE PAR COUPURE.
 *
 * Compter un tiroir, dans la vraie vie, ce n'est pas taper un montant : c'est
 * empiler les billets et les pièces et compter les tas. Ce module porte la
 * table des coupures tunisiennes et l'arithmétique de ces tas.
 *
 * ⚠ TOUT se somme en MILLIMES ENTIERS, jamais en dinars flottants. Trois
 * pièces de 100 millimes valent 0,300 DT ; `0.1 + 0.1 + 0.1` en double vaut
 * 0,30000000000000004. Sur un tiroir de plusieurs centaines de pièces, ces
 * miettes s'accumulent et fabriquent un écart de clôture que personne ne peut
 * expliquer — un écart INVENTÉ PAR LE LOGICIEL. La valeur faciale est donc
 * stockée en entier (`millimes`), la somme reste entière, et la division par
 * 1000 n'arrive qu'une seule fois, tout à la fin.
 */

export type CashDenominationKind = 'note' | 'coin';

export interface CashDenomination {
  /**
   * Identifiant STABLE — c'est la clé écrite dans le JSON du comptage, donc
   * elle est gravée : la renommer rendrait illisibles tous les comptages déjà
   * enregistrés.
   */
  id: string;
  kind: CashDenominationKind;
  /** Valeur faciale en MILLIMES. Entier, TOUJOURS — c'est l'unité de calcul. */
  millimes: number;
  /** Valeur faciale en dinars. Confort d'AFFICHAGE ; jamais un opérande. */
  value: number;
}

function denom(kind: CashDenominationKind, millimes: number): CashDenomination {
  return { id: `${kind}-${millimes}`, kind, millimes, value: millimes / 1000 };
}

/**
 * Coupures du dinar tunisien en circulation, de la plus forte à la plus faible
 * — l'ordre dans lequel on compte un tiroir.
 *
 * Le 5 DT existe EN DOUBLE, en billet et en pièce : les deux ont cours légal et
 * un tiroir contient couramment les deux. Ce sont donc deux lignes distinctes,
 * pas un doublon à nettoyer — les fusionner obligerait le caissier à additionner
 * de tête avant de saisir, exactement ce que ce module existe pour éviter.
 *
 * Les pièces de 5, 10 et 20 millimes ne servent quasiment plus, mais elles ont
 * toujours cours : elles restent proposées (et resteront à zéro dans la plupart
 * des tiroirs) plutôt que d'empêcher un comptage de tomber juste.
 *
 * Volontairement ABSENTS : le billet de 30 DT et la pièce de 1 millime,
 * démonétisés — les proposer inviterait à compter de l'argent qui n'existe plus.
 */
export const TND_DENOMINATIONS: readonly CashDenomination[] = [
  denom('note', 50_000),
  denom('note', 20_000),
  denom('note', 10_000),
  denom('note', 5_000),
  denom('coin', 5_000),
  denom('coin', 2_000),
  denom('coin', 1_000),
  denom('coin', 500),
  denom('coin', 200),
  denom('coin', 100),
  denom('coin', 50),
  denom('coin', 20),
  denom('coin', 10),
  denom('coin', 5),
];

const DENOMINATION_BY_ID: ReadonlyMap<string, CashDenomination> = new Map(
  TND_DENOMINATIONS.map((d) => [d.id, d]),
);

export function findDenomination(id: string): CashDenomination | undefined {
  return DENOMINATION_BY_ID.get(id);
}

/** Coupures d'une nature donnée, dans l'ordre de comptage. */
export function denominationsOfKind(
  kind: CashDenominationKind,
): readonly CashDenomination[] {
  return TND_DENOMINATIONS.filter((d) => d.kind === kind);
}

/**
 * Détail d'un comptage : combien de coupures de chaque sorte.
 *
 * Clé = `CashDenomination.id`, valeur = QUANTITÉ (un entier ≥ 0, pas un
 * montant). Les quantités nulles sont absentes — un objet vide veut dire
 * « aucun détail saisi », pas « tiroir vide ».
 */
export type CashCountBreakdown = Readonly<Record<string, number>>;

/**
 * Nettoie un détail venu de l'extérieur (formulaire, JSONB relu, payload de
 * synchronisation rejoué) et le rend dans l'ordre canonique des coupures.
 *
 * Écarte sans bruit : les clés inconnues, les quantités négatives, non finies,
 * non entières (une demi-pièce n'existe pas) et les zéros. Le résultat est donc
 * TOUJOURS sommable — c'est ce qui permet aux appelants de ne jamais se
 * demander si ce qu'ils tiennent est valide.
 */
export function sanitizeBreakdown(input: unknown): CashCountBreakdown {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const raw = input as Record<string, unknown>;
  const clean: Record<string, number> = {};
  // On itère la TABLE, pas l'entrée : l'ordre du résultat est ainsi celui du
  // comptage, quel que soit l'ordre des clés du JSON reçu.
  for (const d of TND_DENOMINATIONS) {
    const qty = Number(raw[d.id]);
    if (!Number.isInteger(qty) || qty <= 0) continue;
    clean[d.id] = qty;
  }
  return clean;
}

/** `true` quand aucune coupure n'a été saisie — donc « pas de détail ». */
export function isBreakdownEmpty(breakdown: CashCountBreakdown): boolean {
  for (const d of TND_DENOMINATIONS) {
    const qty = breakdown[d.id];
    if (typeof qty === 'number' && Number.isInteger(qty) && qty > 0) return false;
  }
  return true;
}

/**
 * Total du détail, en MILLIMES ENTIERS. C'est LA fonction de vérité : tout le
 * reste en découle.
 */
export function breakdownTotalMillimes(breakdown: CashCountBreakdown): number {
  let total = 0;
  for (const d of TND_DENOMINATIONS) {
    const qty = breakdown[d.id];
    if (!Number.isInteger(qty) || (qty as number) <= 0) continue;
    total += d.millimes * (qty as number);
  }
  return total;
}

/**
 * Total du détail, en dinars. Une SEULE division, sur une somme entière : le
 * résultat est le double le plus proche du montant exact, comme le `round3`
 * utilisé partout ailleurs dans l'ERP.
 */
export function breakdownTotal(breakdown: CashCountBreakdown): number {
  return breakdownTotalMillimes(breakdown) / 1000;
}

/** Nombre de coupures physiques comptées — « 143 pièces et billets ». */
export function breakdownPieceCount(breakdown: CashCountBreakdown): number {
  let pieces = 0;
  for (const d of TND_DENOMINATIONS) {
    const qty = breakdown[d.id];
    if (!Number.isInteger(qty) || (qty as number) <= 0) continue;
    pieces += qty as number;
  }
  return pieces;
}

/** Montant et détail d'un comptage, réconciliés — voir `resolveCountedAmount`. */
export interface ResolvedCashCount {
  /** Montant qui fait foi. `null` = tiroir non compté. */
  amount: number | null;
  /** Détail nettoyé, ou `null` quand rien n'a été saisi coupure par coupure. */
  breakdown: CashCountBreakdown | null;
}

/**
 * Réconcilie un montant saisi et un détail par coupures — LA règle d'arbitrage
 * entre les deux, à appliquer avant toute écriture.
 *
 * **Le détail gagne toujours.** Quand il existe, le montant est RECALCULÉ depuis
 * lui : autrement le stockage pourrait contenir « compté 240,000 DT » à côté
 * d'un détail qui somme à 238,500, et plus rien ne permettrait de savoir lequel
 * des deux est le comptage réel. Ce cas n'est pas théorique — un caissier
 * corrige une quantité après avoir vu le total, une écriture hors-ligne est
 * rejouée telle quelle, une clé de coupure inconnue est écartée au nettoyage.
 *
 * Un détail vide ne veut PAS dire « tiroir à zéro » : il veut dire « pas de
 * détail ». Le montant global, lui, est alors conservé tel quel — y compris
 * `null`, qui garde son sens de tiroir JAMAIS compté (et donc d'écart inconnu,
 * pas d'écart nul).
 */
export function resolveCountedAmount(
  amount: number | null | undefined,
  breakdown: unknown,
): ResolvedCashCount {
  const clean = sanitizeBreakdown(breakdown);
  if (!isBreakdownEmpty(clean)) {
    return { amount: breakdownTotal(clean), breakdown: clean };
  }
  return {
    amount: typeof amount === 'number' && Number.isFinite(amount) ? amount : null,
    breakdown: null,
  };
}

/** Une ligne de détail prête à afficher : la coupure, sa quantité, son sous-total. */
export interface CashBreakdownRow {
  denomination: CashDenomination;
  quantity: number;
  /** Quantité × valeur faciale, en dinars. */
  subtotal: number;
}

/**
 * Lignes NON NULLES d'un détail, dans l'ordre de comptage — ce qu'on imprime
 * sur un rapport Z ou ce qu'on relit sur une fiche de session.
 */
export function breakdownRows(breakdown: CashCountBreakdown): CashBreakdownRow[] {
  const rows: CashBreakdownRow[] = [];
  for (const d of TND_DENOMINATIONS) {
    const qty = breakdown[d.id];
    if (!Number.isInteger(qty) || (qty as number) <= 0) continue;
    rows.push({
      denomination: d,
      quantity: qty as number,
      subtotal: (d.millimes * (qty as number)) / 1000,
    });
  }
  return rows;
}
